import { Localized, useLocalization } from "@fluent/react";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg";
import { APIError, AppId } from "../../../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { impossible } from "../../../../app/Utils";
import { BackgroundProcessLauncher } from "../../../backgroundProcessing/BackgroundProcessLauncher";
import { showError } from "../../../notifications/notificationsSlice";
import { selectBlobsByResourceId } from "../../attachementsSlice";
import { selectDocument } from "../../documentSlice";
import {
    DocumentDqMapping,
    DocumentDqMappingsAiGenerationScope,
    loadDocumentDqMappings,
    runDocumentDqMappingsAiGenerationProcess,
} from "../documentDqMappingsApi";

interface Props {
    documentId: AppId;
    sectionId: AppId;
    disabled?: boolean;
    compact?: boolean;
    onCompleted?: () => void | Promise<void>;
}

export const RunDocumentDqMappingsAiGenerationDialog = ({
    documentId,
    sectionId,
    disabled = false,
    compact = false,
    onCompleted,
}: Props) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const [selectedBlobId, setSelectedBlobId] = useState<string>("");
    const [scope, setScope] = useState<DocumentDqMappingsAiGenerationScope>("ALL");
    const [selectedMappingIds, setSelectedMappingIds] = useState<number[]>([]);
    const [mappings, setMappings] = useState<DocumentDqMapping[]>([]);
    const [mappingSearch, setMappingSearch] = useState("");
    const [loadingMappings, setLoadingMappings] = useState(false);
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState<APIError | null>(null);
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible();

    const blobs = useAppSelector((state) => selectBlobsByResourceId(state, header.guid));
    const hasBlobs = (blobs?.blobs?.length ?? 0) > 0;
    const filteredMappings = useMemo(() => {
        const query = mappingSearch.trim().toLocaleLowerCase();
        if (!query) {
            return mappings;
        }

        return mappings.filter((mapping) => {
            const description = new DOMParser()
                .parseFromString(mapping.requirementDescription ?? "", "text/html")
                .body.textContent ?? "";
            return `${mapping.requirementNumber ?? ""} ${description}`
                .toLocaleLowerCase()
                .includes(query);
        });
    }, [mappingSearch, mappings]);

    const handleStart = useCallback(async () => {
        if (!selectedBlobId) {
            return false;
        }

        try {
            setSaving(true);
            setServerError(null);

            const result = await dispatch(runDocumentDqMappingsAiGenerationProcess({
                documentId,
                payload: {
                    fileId: selectedBlobId,
                    sectionId,
                    documentId: Number(documentId),
                    scope,
                    mappingIds: scope === "SELECTED" ? selectedMappingIds : [],
                },
            }));

            if (runDocumentDqMappingsAiGenerationProcess.fulfilled.match(result)) {
                return true;
            }

            if (result.payload && typeof result.payload === "object" && "kind" in result.payload) {
                setServerError(result.payload as APIError);
            } else {
                dispatch(showError("error"));
            }
        } catch {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }

        return false;
    }, [dispatch, documentId, scope, sectionId, selectedBlobId, selectedMappingIds]);

    const handleOpenQuestion = useCallback(() => {
        setServerError(null);
        setScope("ALL");
        setSelectedMappingIds([]);
        setMappingSearch("");
        setLoadingMappings(true);

        void dispatch(loadDocumentDqMappings({ documentId, sectionId })).then((result) => {
            if (loadDocumentDqMappings.fulfilled.match(result)) {
                setMappings(result.payload);
            } else {
                dispatch(showError("error"));
            }
            setLoadingMappings(false);
        });
    }, [dispatch, documentId, sectionId]);

    const handleCloseQuestion = useCallback(() => {
        setServerError(null);
    }, []);

    const handleMappingToggle = useCallback((mappingId: number) => {
        setSelectedMappingIds((current) => current.includes(mappingId)
            ? current.filter((id) => id !== mappingId)
            : [...current, mappingId]);
    }, []);

    return (
        <BackgroundProcessLauncher
            triggerLabel={
                <Localized id={compact ? "document-dq-mapping-toolbar-generate-ai" : "document-dq-mapping-generate-ai-process"}>
                    <span>{compact ? "_With AI" : "_Generate mappings by AI"}</span>
                </Localized>
            }
            triggerButtonProps={{
                variant: compact ? "outlined" : "contained",
                color: "primary",
                size: "small",
                startIcon: <AutoFixHighIcon />,
            }}
            triggerDisabled={disabled}
            questionTitle={
                <Localized id="document-dq-mapping-generate-ai-process">
                    <span>_Generate mappings by AI</span>
                </Localized>
            }
            questionDescription={
                <Localized id="document-dq-mapping-generate-ai-process-warning">
                    <span>_Start AI generation of mappings based on the selected file?</span>
                </Localized>
            }
            questionChildren={(fullScreen) => (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: fullScreen ? 1 : undefined,
                        minHeight: 0,
                    }}
                >
                    {hasBlobs ? (
                        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <Localized id="choose-file-to-search">
                                    <span>_Wybierz plik do przeszukania</span>
                                </Localized>
                            </Typography>

                            <RadioGroup
                                value={selectedBlobId}
                                onChange={(event) => setSelectedBlobId(event.target.value)}
                            >
                                {blobs?.blobs?.map((blob) => (
                                    <FormControlLabel
                                        key={blob.blobId}
                                        value={blob.blobId}
                                        control={<Radio size="small" />}
                                        label={
                                            <Typography variant="body2" color="text.secondary">
                                                {blob.fileMeta.fileName}
                                            </Typography>
                                        }
                                    />
                                ))}
                            </RadioGroup>
                        </Box>
                    ) : (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <Localized id="document-dq-mapping-no-attachments-warning">
                                <span>_Brak załączników do wyboru</span>
                            </Localized>
                        </Alert>
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            flex: fullScreen && scope === "SELECTED" ? 1 : undefined,
                            minHeight: 0,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            <Localized id="document-dq-mapping-ai-generation-scope">
                                <span>_Generation scope</span>
                            </Localized>
                        </Typography>
                        <RadioGroup
                            value={scope}
                            onChange={(event) => setScope(event.target.value as DocumentDqMappingsAiGenerationScope)}
                        >
                            <FormControlLabel
                                value="ALL"
                                control={<Radio size="small" />}
                                label={<Localized id="document-dq-mapping-ai-generation-scope-all"><span>_All mappings</span></Localized>}
                            />
                            <FormControlLabel
                                value="WITHOUT_MAPPINGS"
                                control={<Radio size="small" />}
                                label={<Localized id="document-dq-mapping-ai-generation-scope-empty"><span>_Mappings without references</span></Localized>}
                            />
                            <FormControlLabel
                                value="SELECTED"
                                control={<Radio size="small" />}
                                label={<Localized id="document-dq-mapping-ai-generation-scope-selected"><span>_Selected mappings</span></Localized>}
                            />
                        </RadioGroup>

                        {scope === "SELECTED" && (
                            <Box
                                sx={{
                                    mx: 2,
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: fullScreen ? 1 : undefined,
                                    minHeight: 0,
                                    border: 1,
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                }}
                            >
                                {loadingMappings ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : mappings.length > 0 ? (
                                    <>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                display: "grid",
                                                gridTemplateColumns: "minmax(0, 1fr) auto",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={l10n.getString("search", null, "Search")}
                                                value={mappingSearch}
                                                onChange={(event) => setMappingSearch(event.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: mappingSearch ? (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                edge="end"
                                                                aria-label={l10n.getString("clear", null, "Clear")}
                                                                title={l10n.getString("clear", null, "Clear")}
                                                                onClick={() => setMappingSearch("")}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ) : undefined,
                                                }}
                                            />
                                            <Localized
                                                id="document-dq-mapping-ai-generation-selected-count"
                                                vars={{ selected: selectedMappingIds.length, total: mappings.length }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        px: 1.25,
                                                        py: 0.5,
                                                        bgcolor: "grey.100",
                                                        border: 1,
                                                        borderColor: "divider",
                                                        borderRadius: 1,
                                                        fontWeight: 600,
                                                        color: "text.primary",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    _Selected {selectedMappingIds.length} of {mappings.length}
                                                </Typography>
                                            </Localized>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "40px minmax(90px, 160px) minmax(0, 1fr)",
                                                alignItems: "center",
                                                px: 1,
                                                py: 0.75,
                                                bgcolor: "action.hover",
                                                borderTop: 1,
                                                borderBottom: 1,
                                                borderColor: "divider",
                                            }}
                                        >
                                            <Box />
                                            <Typography variant="caption" fontWeight="bold">
                                                <Localized id="urs-requirement-no">
                                                    <span>_Code</span>
                                                </Localized>
                                            </Typography>
                                            <Typography variant="caption" fontWeight="bold">
                                                <Localized id="document-dq-mapping-ai-generation-column-content">
                                                    <span>_Content</span>
                                                </Localized>
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                height: fullScreen ? "auto" : 240,
                                                flex: fullScreen ? 1 : undefined,
                                                minHeight: 0,
                                                overflowY: "auto",
                                            }}
                                        >
                                            {filteredMappings.map((mapping) => (
                                                <Box
                                                    key={mapping.id}
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: "40px minmax(90px, 160px) minmax(0, 1fr)",
                                                        alignItems: "start",
                                                        px: 1,
                                                        py: 0.75,
                                                        borderBottom: 1,
                                                        borderColor: "divider",
                                                        bgcolor: selectedMappingIds.includes(mapping.id) ? "grey.100" : "background.paper",
                                                        "&:last-child": { borderBottom: 0 },
                                                        "&:hover": {
                                                            bgcolor: selectedMappingIds.includes(mapping.id) ? "grey.200" : "action.hover",
                                                        },
                                                    }}
                                                >
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedMappingIds.includes(mapping.id)}
                                                        onChange={() => handleMappingToggle(mapping.id)}
                                                        inputProps={{ "aria-label": mapping.requirementNumber || `#${mapping.id}` }}
                                                    />
                                                    <Typography variant="body2" fontWeight="medium" sx={{ pt: 0.75, pr: 1 }}>
                                                        {mapping.requirementNumber || `#${mapping.id}`}
                                                    </Typography>
                                                    <Typography
                                                        component="div"
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ pt: 0.75, pr: 1, "& p": { m: 0 } }}
                                                        dangerouslySetInnerHTML={{ __html: mapping.requirementDescription }}
                                                    />
                                                </Box>
                                            ))}
                                            {filteredMappings.length === 0 && (
                                                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                                                    <Localized id="document-dq-mapping-ai-generation-no-search-results">
                                                        <span>_No matching requirements</span>
                                                    </Localized>
                                                </Typography>
                                            )}
                                        </Box>
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        <Localized id="document-dq-mapping-ai-generation-no-mappings">
                                            <span>_No mappings available</span>
                                        </Localized>
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>

                    <ServerErrorMsg err={serverError} />
                </Box>
            )}
            questionMaxWidth="md"
            questionAllowFullScreen
            trackingLabel="Generate DQ mappings by AI"
            loading={saving}
            confirmLabel="Generate"
            confirmLabelId="generate"
            confirmColor="info"
            confirmStartIcon={<AutoFixHighIcon />}
            confirmDisabled={
                !hasBlobs
                || !selectedBlobId
                || (scope === "SELECTED" && (loadingMappings || selectedMappingIds.length === 0))
            }
            objectType="DOCUMENT"
            objectId={documentId.toString()}
            onStart={handleStart}
            onCompleted={onCompleted}
            onOpenQuestion={handleOpenQuestion}
            onCloseQuestion={handleCloseQuestion}
        />
    );
};